// src/pages/B2B/crm/CalendarPage.tsx
import React, { useMemo, useState } from 'react';
import { getAdminApiUrl } from '@/pages/Admin/shared';
import { CRMSubTabs } from './components/CRMSubTabs';
import { MonthGrid } from './components/MonthGrid';
import { AgendaList } from './components/AgendaList';
import { MeetingModal } from './components/MeetingModal';
import { useMeetingList } from './api/meetingsClient';
import { useVenueList } from './api/crmClient';
import { Meeting } from './api/types';

type ViewMode = 'month' | 'agenda';

function startOfMonthGrid(anchor: Date): Date {
  // Sunday-aligned (per spec).
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const dayOfWeek = first.getDay();
  const start = new Date(first);
  start.setDate(first.getDate() - dayOfWeek);
  return start;
}

function CalendarPage(): React.ReactElement {
  const apiUrl = getAdminApiUrl('prod');
  const [monthAnchor, setMonthAnchor] = useState(() => new Date());
  const [view, setView] = useState<ViewMode>('month');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Meeting | null>(null);
  const [defaultDate, setDefaultDate] = useState<Date | undefined>(undefined);

  const window = useMemo(() => {
    const start = startOfMonthGrid(monthAnchor);
    const end = new Date(start);
    end.setDate(start.getDate() + 42);
    return {
      from: start.toISOString(),
      to: end.toISOString(),
    };
  }, [monthAnchor]);

  const { data: meetingData, loading: mLoading, error: mError, refetch } =
    useMeetingList(apiUrl, window);
  const { data: venueData } = useVenueList(apiUrl, {});

  const monthLabel = monthAnchor.toLocaleDateString([], { month: 'long', year: 'numeric' });

  const openCreate = (day?: Date) => {
    setEditing(null);
    setDefaultDate(day);
    setModalOpen(true);
  };
  const openEdit = (m: Meeting) => {
    setEditing(m);
    setDefaultDate(undefined);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setDefaultDate(undefined);
  };

  const prevMonth = () => setMonthAnchor(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const nextMonth = () => setMonthAnchor(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  const today = () => setMonthAnchor(new Date());

  return (
    <div className="min-h-screen bg-[#0f0f1a]">
      <div className="max-w-7xl mx-auto px-6 pt-12 pb-8">
        <div className="flex justify-between items-end mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white font-display">CRM</h1>
            <p className="mt-2 text-gray-400 text-sm">Venue pipeline, contacts, and outreach tracking.</p>
          </div>
          <button
            type="button"
            onClick={() => openCreate()}
            className="bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold rounded-md px-4 py-2"
          >
            + New Meeting
          </button>
        </div>

        <CRMSubTabs active="calendar" />

        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <button onClick={prevMonth} className="text-gray-400 hover:text-gray-200 text-sm px-2 py-1">‹</button>
            <div className="text-white text-sm font-semibold w-44 text-center">{monthLabel}</div>
            <button onClick={nextMonth} className="text-gray-400 hover:text-gray-200 text-sm px-2 py-1">›</button>
            <button onClick={today} className="text-gray-400 hover:text-gray-200 text-xs px-2 py-1 border border-[#2a2a3e] rounded">Today</button>
          </div>
          <div className="flex items-center gap-1 text-xs">
            <button onClick={() => setView('month')}
                    className={`px-3 py-1.5 rounded ${view === 'month' ? 'bg-[#1a1a2e] text-violet-300 border border-violet-500/40' : 'text-gray-500'}`}>
              Month
            </button>
            <button onClick={() => setView('agenda')}
                    className={`px-3 py-1.5 rounded ${view === 'agenda' ? 'bg-[#1a1a2e] text-violet-300 border border-violet-500/40' : 'text-gray-500'}`}>
              Agenda
            </button>
          </div>
        </div>

        {mError && (
          <div className="bg-red-900/20 border border-red-700/40 text-red-300 rounded-md p-3 text-sm mb-4">
            {mError}
          </div>
        )}

        {mLoading && (
          <div className="text-gray-500 text-sm py-8 text-center">Loading…</div>
        )}

        {!mLoading && view === 'month' && (
          <MonthGrid
            monthAnchor={monthAnchor}
            meetings={meetingData.meetings}
            onDayClick={openCreate}
            onEventClick={openEdit}
          />
        )}

        {!mLoading && view === 'agenda' && (
          <AgendaList
            meetings={meetingData.meetings}
            venues={venueData.venues}
            onEventClick={openEdit}
          />
        )}

        <MeetingModal
          apiUrl={apiUrl}
          venues={venueData.venues}
          open={modalOpen}
          onClose={closeModal}
          onSaved={refetch}
          meeting={editing}
          defaultDate={defaultDate}
        />
      </div>
    </div>
  );
}

export default CalendarPage;
